<template>
  <!-- Wave 16 — same drawer-layering fix as CreateStripeCreditNoteModal: the picker opens from
       inside the (now body-teleported, z 10035) Price Breakdown drawer, so it needs its own
       stacking context above the drawer layer to be visible. -->
  <Teleport to="body">
  <div v-if="modelValue" class="fixed inset-0 z-[10050]">
  <Dialog
    title="Select invoice to credit"
    :custom-title="true"
    :show-close-button="true"
    :show-footer="false"
    :lg="false"
    :md="true"
    :sm="false"
    custom-width-class="!max-w-[min(40rem,96vw)]"
    @close-event="close"
  >
    <template #title>
      <div class="flex min-w-0 flex-col gap-0.5 pr-2">
        <h4 class="text-lg font-semibold leading-tight text-ink">
          Select invoice to credit
        </h4>
        <p class="text-sm font-medium text-ink-soft">
          Order #{{ orderLabel }}
        </p>
      </div>
    </template>
    <template #modal-content>
      <p class="mb-3 text-sm text-ink-soft">
        Stripe requires a credit note to target one invoice at a time. Pick the invoice you want to credit.
      </p>

      <div v-if="loading" class="flex items-center justify-center py-10">
        <span class="border-[3px] border-[#219f84] rounded-full w-8 h-8 animate-spin border-r-transparent" />
        <span class="ml-3 text-sm text-ink-soft">Checking eligibility…</span>
      </div>

      <ul v-else class="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-surface">
        <li
          v-for="row in rows"
          :key="'pick-' + row.invoiceId"
          class="flex items-center justify-between gap-3 px-3 py-3"
          :class="row.canCredit ? '' : 'bg-surface-2'"
        >
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
              <span class="truncate text-sm font-semibold text-ink">
                {{ row.invoiceNumber || row.stripeInvoiceId }}
              </span>
              <span
                v-if="row.statusLabel"
                class="inline-flex items-center rounded-md border border-slate-300 bg-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-soft"
              >
                {{ row.statusLabel }}
              </span>
            </div>
            <div class="mt-0.5 text-xs text-ink-soft">
              <template v-if="row.eligibility">
                <div class="truncate">
                  Total {{ formatMoney(row.eligibility.invoice.total_minor, row.eligibility.currency) }} {{ row.eligibility.currency }}
                  <template v-if="row.eligibility.can_credit">
                    · Remaining creditable
                    <span class="font-medium text-ink">
                      {{ formatMoney(row.eligibility.remaining_creditable_minor, row.eligibility.currency) }} {{ row.eligibility.currency }}
                    </span>
                  </template>
                  <template v-else-if="row.disableLabel">
                    · {{ row.disableLabel }}
                  </template>
                </div>
              </template>
              <template v-else-if="row.error">
                <!-- Error wraps (no truncate) so the full reason is visible inline; title on hover -->
                <span class="block break-words text-rose-600" :title="row.error">{{ row.error }}</span>
              </template>
            </div>
          </div>
          <button
            type="button"
            class="shrink-0 rounded-md border border-[#219f84] px-3 py-1.5 text-sm font-medium text-[#219f84] transition hover:bg-[#219f84] hover:text-white disabled:cursor-not-allowed disabled:border-slate-300 disabled:text-ink-mute disabled:hover:bg-surface disabled:hover:text-ink-mute"
            :disabled="!row.canCredit"
            :title="!row.canCredit && row.disableLabel ? row.disableLabel : undefined"
            @click="pick(row)"
          >
            Credit
          </button>
        </li>
        <li v-if="rows.length === 0" class="px-3 py-6 text-center text-sm text-ink-mute">
          No invoices on this order yet.
        </li>
      </ul>
    </template>
  </Dialog>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import Dialog from '@/components/Shared/Dialog.vue';
import {
  type CreditNoteEligibility,
  disableReasonLabel,
  extractCreditNoteErrorMessage,
  fetchInvoiceCreditEligibility,
  formatMinorAsDisplay,
  isCreditableInvoiceRow,
} from '@/utils/stripeCreditNote';

interface PartialInvoiceRow {
  id?: number | string;
  stripe_invoice_id?: string;
  stripe_invoice_number?: string | null;
  status?: string | null;
}

const props = defineProps<{
  modelValue: boolean;
  orderId: number;
  orderLabel: string | number;
  partialInvoices: PartialInvoiceRow[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'picked', payload: { invoice: PartialInvoiceRow; eligibility: CreditNoteEligibility }): void;
}>();

interface PickerRow {
  invoiceId: number;
  invoice: PartialInvoiceRow;
  invoiceNumber: string | null;
  stripeInvoiceId: string;
  statusLabel: string;
  canCredit: boolean;
  disableLabel: string;
  eligibility: CreditNoteEligibility | null;
  error: string | null;
}

const loading = ref(false);
const rows = ref<PickerRow[]>([]);

function close(): void {
  emit('update:modelValue', false);
}

function pick(row: PickerRow): void {
  if (!row.canCredit || !row.eligibility) return;
  emit('picked', { invoice: row.invoice, eligibility: row.eligibility });
  close();
}

function formatMoney(minor: number, currency: string): string {
  return formatMinorAsDisplay(minor, currency);
}

function buildBaseRow(inv: PartialInvoiceRow): PickerRow {
  return {
    invoiceId: Number(inv.id ?? 0),
    invoice: inv,
    invoiceNumber: inv.stripe_invoice_number || null,
    stripeInvoiceId: String(inv.stripe_invoice_id || ''),
    statusLabel: inv.status ? String(inv.status) : '',
    canCredit: false,
    disableLabel: '',
    eligibility: null,
    error: null,
  };
}

async function loadEligibility(): Promise<void> {
  // Rows without a real stripe_invoice_id (saga pending/failed) always 422 on eligibility —
  // the parent already filters them out, this is defense-in-depth for other callers.
  const invoices = props.partialInvoices.filter((inv) => isCreditableInvoiceRow(inv as Record<string, unknown>));
  rows.value = invoices.map(buildBaseRow);
  if (invoices.length === 0) {
    return;
  }
  loading.value = true;
  try {
    const results = await Promise.allSettled(
      invoices.map((inv) => fetchInvoiceCreditEligibility(props.orderId, Number(inv.id))),
    );
    results.forEach((res, idx) => {
      const row = rows.value[idx];
      if (!row) return;
      if (res.status === 'fulfilled') {
        row.eligibility = res.value;
        row.canCredit = res.value.can_credit;
        row.disableLabel = disableReasonLabel(res.value.disable_reason);
      } else {
        // Surface the actual reason (Laravel `{message, errors}` shape or axios fallback)
        // so the picker explains *why* eligibility failed instead of a generic message.
        const detail = extractCreditNoteErrorMessage(res.reason);
        row.error = `Could not load eligibility (${detail})`;
      }
    });
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      void loadEligibility();
    }
  },
  { immediate: true },
);

</script>
